// Utility to compute a simple, copyright-safe game icon badge
// We avoid shipping copyrighted logos; instead, render initials on a colored badge.

export function gameInitials(name) {
  if (!name) return "🎮";
  const parts = String(name)
    .replace(/[^\p{L}\p{N}\s]+/gu, " ")
    .trim()
    .split(/\s+/)
    .slice(0, 3);
  const letters = parts.map(p => p[0]).join("").toUpperCase();
  return letters || "🎮";
}

export function gameColor(name) {
  const str = String(name || "game");
  // Simple hash to color
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  const bg = `hsl(${hue} 60% 35%)`;
  const fg = `hsl(${hue} 90% 92%)`;
  return { bg, fg };
}

import { resolveMediaUrl } from './url';

export function avatarUrlAbs(avatarUrl) {
  if (!avatarUrl) return undefined;
  if (/^https?:\/\//i.test(avatarUrl) || avatarUrl.startsWith('data:')) return avatarUrl;
  return resolveMediaUrl(avatarUrl);
}

export function userInitials(displayName, username) {
  const src = displayName || username || '';
  if (!src) return 'U';
  const parts = src.trim().split(/\s+/).slice(0, 2);
  const letters = parts.map(p => p[0]).join('').toUpperCase();
  return letters || 'U';
}

export function userColor(displayName, username) {
  const str = String(displayName || username || 'user');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
    hash |= 0;
  }
  const hue = Math.abs(hash) % 360;
  const bg = `hsl(${hue} 40% 35%)`;
  const fg = `hsl(${hue} 80% 90%)`;
  return { bg, fg };
}

// Compute a public path for a local game icon if available
export function getGameIconPath(name) {
  if (!name) return '/game-icons/_default.svg';
  const slug = String(name)
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `/game-icons/${slug}.svg`;
}

// --- RAWG integration (client-side, ephemeral) ---
// We only use this for display. No server-side persistence.
const rawgCache = new Map(); // name -> { url, ts }

function slugify(name) {
  return String(name || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export async function fetchRawgImageUrl(gameName) {
  if (!gameName) return null;
  const key = gameName.trim();
  const now = Date.now();
  const cached = rawgCache.get(key);
  if (cached && now - cached.ts < 1000 * 60 * 60 * 24) { // 24h
    return cached.url;
  }
  try {
    // Call backend proxy to avoid CORS and hide key
    const base = import.meta.env.VITE_API_URL || '';
    const url = `${base}/api/rawg/image?` + new URLSearchParams({ query: key }).toString();
    const res = await fetch(url, { credentials: 'include' });
    if (!res.ok) throw new Error('RAWG request failed');
    const data = await res.json();
    const img = data?.imageUrl || null;
    if (img) {
      rawgCache.set(key, { url: img, ts: now });
      return img;
    }
  } catch (e) {
    // ignore errors, fallback later
  }
  rawgCache.set(key, { url: null, ts: now });
  return null;
}
